import { isSemanticModelClassProfile, isSemanticModelRelationshipProfile, SEMANTIC_MODEL_CLASS_PROFILE, SEMANTIC_MODEL_RELATIONSHIP_PROFILE, } from "../concepts/index.js";
import { isCreateSemanticModelClassProfile, isModifySemanticModelClassProfile, isCreateSemanticModelRelationshipProfile, isModifySemanticModelRelationshipProfile, isAddControlledVocabularyAssignment, isRemoveControlledVocabularyAssignment, isModifyControlledVocabularyAssignment, isModifySemanticModelRelationshipEndProfile } from "./operations.js";
class DefaultSemanticModelProfileOperationExecutor {
    entityReader;
    entityWriter;
    constructor(entityReader, entityWriter) {
        this.entityReader = entityReader;
        this.entityWriter = entityWriter;
    }
    executeOperation(operation) {
        if (isCreateSemanticModelClassProfile(operation)) {
            return executeCreateSemanticModelClassProfile(this.entityWriter, operation);
        }
        if (isModifySemanticModelClassProfile(operation)) {
            return executeModifySemanticModelClassProfile(this.entityReader, this.entityWriter, operation);
        }
        if (isCreateSemanticModelRelationshipProfile(operation)) {
            return executeCreateSemanticModelRelationshipProfile(this.entityWriter, operation);
        }
        if (isModifySemanticModelRelationshipProfile(operation)) {
            return executeModifySemanticModelRelationshipProfile(this.entityReader, this.entityWriter, operation);
        }
        if (isAddControlledVocabularyAssignment(operation)) {
            return executeAddControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isRemoveControlledVocabularyAssignment(operation)) {
            return executeRemoveControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isModifyControlledVocabularyAssignment(operation)) {
            return executeModifyControlledVocabularyAssignment(this.entityReader, this.entityWriter, operation);
        }
        if (isModifySemanticModelRelationshipEndProfile(operation)) {
            return executeModifySemanticModelRelationshipEndProfile(this.entityReader, this.entityWriter, operation);
        }
        return null;
    }
}
function executeCreateSemanticModelClassProfile(entityWriter, { entity }) {
    const identifier = entity.id;
    const newEntity = {
        ...defaultClassProfile(),
        ...entity,
        id: identifier,
        type: [SEMANTIC_MODEL_CLASS_PROFILE],
    };
    entityWriter.change({ [identifier]: newEntity }, []);
    return {
        success: true,
        created: [identifier],
    };
}
function defaultClassProfile() {
    return {
        iri: null,
        name: null,
        nameFromProfiled: null,
        description: null,
        descriptionFromProfiled: null,
        usageNote: null,
        usageNoteFromProfiled: null,
        profiling: [],
        externalDocumentationUrl: null,
        tags: [],
        controlledVocabularies: [],
    };
}
function executeModifySemanticModelClassProfile(entityReader, entityWriter, { identifier, entity }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelClassProfile(previous)) {
        console.error("Previous values is not class profile, action to update the profile is ignored.", { previous, next: entity });
        return {
            success: false,
            created: [],
        };
    }
    ;
    const updatedEntity = {
        // The actual entity can be more specialized than SemanticModelClassProfile
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_CLASS_PROFILE],
        description: entity.description ?? previous.description,
        descriptionFromProfiled: mergeFromProfiled(entity.descriptionFromProfiled, previous.descriptionFromProfiled),
        name: entity.name ?? previous.name,
        nameFromProfiled: mergeFromProfiled(entity.nameFromProfiled, previous.nameFromProfiled),
        iri: entity.iri ?? previous.iri,
        usageNote: entity.usageNote ?? previous.usageNote,
        usageNoteFromProfiled: mergeFromProfiled(entity.usageNoteFromProfiled, previous.usageNoteFromProfiled),
        profiling: entity.profiling ?? previous.profiling,
        externalDocumentationUrl: mergeFromProfiled(entity.externalDocumentationUrl, previous.externalDocumentationUrl),
        tags: mergeFromProfiled(entity.tags, previous.tags),
        order: mergeFromProfiled(entity.order, previous.order) ?? null,
        controlledVocabularies: mergeFromProfiled(entity.controlledVocabularies, previous.controlledVocabularies),
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
function mergeFromProfiled(next, previous) {
    // We need to store null, if next is null.
    if (next === undefined) {
        return previous;
    }
    return next;
}
function executeCreateSemanticModelRelationshipProfile(entityWriter, { entity }) {
    const identifier = entity.id;
    const newEntity = {
        ...entity,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends: (entity.ends ?? []).map(item => ({
            ...defaultRelationshipEndProfile(),
            ...item,
        })),
    };
    entityWriter.change({ [identifier]: newEntity }, []);
    return {
        success: true,
        created: [identifier],
    };
}
function defaultRelationshipEndProfile() {
    return {
        name: null,
        nameFromProfiled: null,
        description: null,
        descriptionFromProfiled: null,
        iri: null,
        cardinality: null,
        usageNote: null,
        usageNoteFromProfiled: null,
        profiling: [],
        externalDocumentationUrl: null,
        tags: [],
        order: null,
    };
}
function executeModifySemanticModelRelationshipProfile(entityReader, entityWriter, { identifier, entity }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelRelationshipProfile(previous)) {
        console.error("Previous values is not relationship profile, action to update the profile is ignored.", { previous, next: entity });
        return {
            success: false,
            created: [],
        };
    }
    ;
    // When no ends are give, use the one from previous state
    const ends = entity.ends === undefined ? previous.ends :
        // Else we merge old to new, otherwise we would not be able to delete.
        entity.ends.map((value, index) => ({
            ...(previous.ends[index] ?? {}),
            ...value
        }));
    const updatedEntity = {
        // The actual entity can be more specialized than SemanticModelRelationshipProfile
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends,
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
// TODO: should we do validations here?
// - only one CV with MUST on profile
// - inherited qualifiers can be only changed to stricter
function executeAddControlledVocabularyAssignment(entityReader, entityWriter, { classProfileIdentifier, assignment }) {
    const previous = entityReader.entity(classProfileIdentifier);
    if (previous === null || !isSemanticModelClassProfile(previous)) {
        console.error("Target is not a class profile, add controlled vocabulary assignment is ignored.", { previous });
        return { success: false, created: [] };
    }
    const existing = previous.controlledVocabularies ?? [];
    if (existing.some(a => a.identifier === assignment.identifier)) {
        console.error("controlledVocabularyIdentifier is already assigned to this class profile, add controlled vocabulary assignment is ignored.", { controlledVocabularyIdentifier: assignment.identifier });
        return { success: false, created: [] };
    }
    const updatedEntity = {
        ...previous,
        controlledVocabularies: [...existing, assignment],
    };
    entityWriter.change({ [classProfileIdentifier]: updatedEntity }, []);
    return { success: true, created: [] };
}
function executeRemoveControlledVocabularyAssignment(entityReader, entityWriter, { classProfileIdentifier, controlledVocabularyIdentifier }) {
    const previous = entityReader.entity(classProfileIdentifier);
    if (previous === null || !isSemanticModelClassProfile(previous)) {
        console.error("Target is not a class profile, remove controlled vocabulary assignment is ignored.", { previous });
        return { success: false, created: [] };
    }
    const existing = previous.controlledVocabularies ?? [];
    if (!existing.some(a => a.identifier === controlledVocabularyIdentifier)) {
        console.error("controlledVocabularyIdentifier not found in class profile, remove controlled vocabulary assignment is ignored.", { controlledVocabularyIdentifier });
        return { success: false, created: [] };
    }
    const updatedEntity = {
        ...previous,
        controlledVocabularies: existing.filter(a => a.identifier !== controlledVocabularyIdentifier),
    };
    entityWriter.change({ [classProfileIdentifier]: updatedEntity }, []);
    return { success: true, created: [] };
}
function executeModifyControlledVocabularyAssignment(entityReader, entityWriter, { classProfileIdentifier, controlledVocabularyIdentifier, changes }) {
    const previous = entityReader.entity(classProfileIdentifier);
    if (previous === null || !isSemanticModelClassProfile(previous)) {
        console.error("Target is not a class profile, modify controlled vocabulary assignment is ignored.", { previous });
        return { success: false, created: [] };
    }
    const existing = previous.controlledVocabularies ?? [];
    if (!existing.some(a => a.identifier === controlledVocabularyIdentifier)) {
        console.error("controlledVocabularyIdentifier not found in class profile, modify controlled vocabulary assignment is ignored.", { controlledVocabularyIdentifier });
        return { success: false, created: [] };
    }
    const updatedEntity = {
        ...previous,
        controlledVocabularies: existing.map(a => a.identifier === controlledVocabularyIdentifier ? { ...a, ...changes } : a),
    };
    entityWriter.change({ [classProfileIdentifier]: updatedEntity }, []);
    return { success: true, created: [] };
}
function executeModifySemanticModelRelationshipEndProfile(entityReader, entityWriter, { identifier, endIndex, end }) {
    const previous = entityReader.entity(identifier);
    if (previous === null || !isSemanticModelRelationshipProfile(previous)) {
        console.error("Previous value is not relationship profile, action to update the end profile is ignored.", { previous, next: end });
        return {
            success: false,
            created: [],
        };
    }
    const previousEnd = previous.ends[endIndex];
    if (previousEnd === undefined) {
        console.error("End index out of bounds, action to update the end profile is ignored.", { endIndex, ends: previous.ends });
        return {
            success: false,
            created: [],
        };
    }
    const ends = [...previous.ends];
    ends[endIndex] = { ...previousEnd, ...end };
    const updatedEntity = {
        ...previous,
        id: identifier,
        type: [SEMANTIC_MODEL_RELATIONSHIP_PROFILE],
        ends,
    };
    entityWriter.change({ [identifier]: updatedEntity }, []);
    return {
        success: true,
        created: [],
    };
}
export function createDefaultSemanticModelProfileOperationExecutor(entityReader, entityWriter) {
    return new DefaultSemanticModelProfileOperationExecutor(entityReader, entityWriter);
}
//# sourceMappingURL=operations-executor.js.map